import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  House,
  MapTrifold,
  Drop,
  UsersThree,
  CreditCard,
  Upload,
  SignOut,
  List as MenuIcon,
  X,
  ArrowUpRight,
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { PlantusLogo } from "@/components/plantus/Logo";

const navItems = [
  { to: "/app", label: "Übersicht", icon: House, end: true, testId: "nav-dash" },
  { to: "/app/fields", label: "Flächen", icon: MapTrifold, testId: "nav-fields" },
  { to: "/app/applications", label: "Anwendungen", icon: Drop, testId: "nav-applications" },
  { to: "/app/import", label: "Import", icon: Upload, testId: "nav-import" },
  { to: "/app/team", label: "Team", icon: UsersThree, testId: "nav-team" },
  { to: "/app/abo", label: "Abonnement", icon: CreditCard, testId: "nav-billing" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const onLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-bone-100/40 flex" data-testid="app-shell">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static z-40 inset-y-0 left-0 w-64 bg-white border-r border-bone-200 flex flex-col transform transition-transform ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="px-6 py-6 border-b border-bone-200 flex items-center justify-between">
          <PlantusLogo size="md" />
          <button
            className="lg:hidden p-1 text-forest-700"
            onClick={() => setOpen(false)}
            aria-label="Schließen"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testId}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl font-body text-sm transition-colors ${
                  isActive
                    ? "bg-forest-50 text-forest-700 font-semibold"
                    : "text-[#4A524C] hover:bg-bone-100"
                }`
              }
            >
              <item.icon size={18} weight="duotone" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="px-4 py-4 border-t border-bone-200 space-y-3">
          <div className="px-2">
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#7A827C]">
              Angemeldet
            </div>
            <div className="font-heading text-sm font-semibold text-forest-900 truncate">
              {user?.name}
            </div>
            <div className="font-body text-xs text-[#4A524C] truncate">
              {user?.company_name}
            </div>
          </div>
          <button
            type="button"
            onClick={onLogout}
            data-testid="logout-button"
            className="w-full inline-flex items-center gap-2 text-sm text-[#4A524C] hover:text-forest-700 px-2 py-1.5 rounded-lg hover:bg-bone-100"
          >
            <SignOut size={16} weight="bold" />
            Abmelden
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {open && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white/80 backdrop-blur border-b border-bone-200 px-4 sm:px-8 py-4 flex items-center justify-between gap-3 lg:hidden">
          <button
            className="p-1 text-forest-700"
            onClick={() => setOpen(true)}
            aria-label="Menü"
          >
            <MenuIcon size={22} />
          </button>
          <PlantusLogo size="sm" />
          <a
            href="/"
            className="inline-flex items-center gap-1 text-xs text-forest-700 hover:underline"
          >
            Website
            <ArrowUpRight size={14} />
          </a>
        </header>
        <main className="flex-1 px-4 sm:px-8 py-8 lg:py-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
